import type { ReactNode } from 'react';
import { Children, Fragment, isValidElement } from 'react';

import { ArrowLeft, X } from '../../icons';
import { Platform } from '../../platform';
import { Text } from '../../primitives/Text';
import { View } from '../../primitives/View';
import { useTheme } from '../../theme';
import { cn } from '../../utils/cn';
import { subTestID } from '../../utils/testId';
import { Button, type IconComponent } from '../Button';
import {
  DIALOG_HEADER_ICON_CHIP_CLASS,
  DIALOG_HEADER_ICON_SIZE,
  DIALOG_HEADER_MEDIA_CLASS,
  dialogBodyContainerVariants,
  type DialogBodySurface,
  dialogBodyTextVariants,
  type DialogFooterVariantProps,
  dialogFooterVariants,
  dialogTitleVariants,
} from './Dialog.variants';
import { useDialogContext } from './DialogContext';

/**
 * Dialog compound parts — the composable, shadcn-Dialog-style API.
 *
 * `Dialog` renders the overlay + glass panel; everything inside is up to the
 * caller. The convenience props on `Dialog` (title, footer, headerIcon, …)
 * render THROUGH these same parts, so prop-driven and composed modals cannot
 * drift apart. All parts read alignment and the dismiss handler from
 * `DialogContext`, provided by the panel.
 *
 * ```tsx
 * <Dialog open={open} onClose={close} size="md" accessibilityLabel={t('…')}>
 *   <DialogHeader>
 *     <DialogTitle>{t('profile.completeTitle')}</DialogTitle>
 *     <DialogSubheading>{t('profile.completeHint')}</DialogSubheading>
 *   </DialogHeader>
 *   <Input label={t('profile.username')} />
 *   <DialogFooter>
 *     <Button variant="secondary" size="xl">{t('common.cancel')}</Button>
 *     <Button variant="primary" size="xl">{t('common.save')}</Button>
 *   </DialogFooter>
 * </Dialog>
 * ```
 */

/** Header block — stacks `DialogTitle` + `DialogSubheading` at the Figma 4px gap. */
export function DialogHeader({
  children,
  className,
}: {
  children?: ReactNode;
  className?: string;
}) {
  const { align } = useDialogContext();
  return (
    <View className={cn('w-full gap-1', align === 'center' && 'items-center', className)}>
      {children}
    </View>
  );
}

/** Title — DS H1 (24/32) in Mona Sans Bold, one size on every panel. */
export function DialogTitle({
  children,
  className,
  testID,
}: {
  children?: ReactNode;
  className?: string;
  /** Defaults to the dialog's `<testID>-title`. */
  testID?: string;
}) {
  const { align, testID: rootTestID } = useDialogContext();
  return (
    <Text
      testID={testID ?? subTestID(rootTestID, 'title')}
      className={cn(dialogTitleVariants({ align }), className)}>
      {children}
    </Text>
  );
}

/** Subheading / description — Figma B1 (Mona Sans Medium 16/24, secondary-600). */
export function DialogSubheading({
  children,
  className,
  testID,
}: {
  children?: ReactNode;
  className?: string;
  /** Defaults to the dialog's `<testID>-subheading`. */
  testID?: string;
}) {
  const { align, testID: rootTestID } = useDialogContext();
  return (
    <Text
      testID={testID ?? subTestID(rootTestID, 'subheading')}
      className={cn(dialogBodyTextVariants({ align }), className)}>
      {children}
    </Text>
  );
}

/**
 * Body — string children get the Figma Text-template style automatically;
 * anything else renders inside a plain full-width column.
 *
 * By default the body FILLS the panel: full width plus `flex-1`, so the spare
 * height the panel's per-size min-height can leave (Dialog.variants.ts) lands
 * inside the body instead of as a gap above the footer. The body never
 * shrinks below its content (the View reset pins `flex-shrink: 0`), so long
 * interiors overflow-scroll the PANEL on web — `Dialog bodyScroll` flips that
 * so the body scrolls and the footer stays. Pass `fill={false}` for a body
 * that should hug its content instead.
 *
 * `contained` renders the Figma "body container": a solid secondary-50
 * surface (1px secondary-200 border, 24px radius/padding) so rich content —
 * forms, grids, lists, previews — doesn't sit directly on the glass panel.
 * Footer actions (`DialogFooter` / the `footer` prop) always stay outside.
 *
 * `surface` picks which container FILL to use and implies `contained`, so
 * `<DialogBody surface="canvas">` is all a dotted-workspace body needs. See
 * `dialogBodyContainerVariants` for the flavours.
 */
export function DialogBody({
  children,
  className,
  contained = false,
  surface,
  fill = true,
  testID,
}: {
  children?: ReactNode;
  className?: string;
  /** Defaults to the dialog's `<testID>-body`. */
  testID?: string;
  /** Shorthand for `surface="solid"`. */
  contained?: boolean;
  /** Container fill. Implies `contained`. */
  surface?: DialogBodySurface;
  /** Grow to absorb the panel's spare height. Default true. */
  fill?: boolean;
}) {
  const { align, testID: rootTestID } = useDialogContext();
  const bodyTestID = testID ?? subTestID(rootTestID, 'body');
  // One style axis: `surface` names the flavour, `contained` is the boolean
  // shorthand for the default one. Null = no container, body sits on glass.
  const containerSurface = surface ?? (contained ? 'solid' : null);
  const containerClass =
    containerSurface && dialogBodyContainerVariants({ surface: containerSurface });
  // `grow`, not `flex-1`: RN's Yoga (classic errata) doesn't count a
  // flex-basis:0 child toward an auto-height parent, so `flex-1` froze the
  // panel at its per-size min-height and let long bodies overflow the footer.
  const fillClass = fill ? 'grow' : undefined;
  if (typeof children === 'string') {
    const text = (
      <Text
        testID={containerClass ? undefined : bodyTestID}
        className={cn(
          dialogBodyTextVariants({ align }),
          !containerClass && fillClass,
          !containerClass && className,
        )}>
        {children}
      </Text>
    );
    return containerClass ? (
      <View testID={bodyTestID} className={cn(containerClass, fillClass, className)}>
        {text}
      </View>
    ) : (
      text
    );
  }
  return (
    <View
      testID={bodyTestID}
      className={cn(containerClass ?? 'w-full', fillClass, className)}>
      {children}
    </View>
  );
}

/**
 * Footer — 8px gap; `layout` maps the Figma "Footer style" arrangements
 * (see `dialogFooterVariants`). Defaults from alignment: `end` for left,
 * `split` (equal-width pair) for center. `split` wraps each child in a
 * flex-1 box so buttons stretch evenly.
 */
/**
 * `Children.map`/`toArray` treat a fragment as ONE child, and callers pass the
 * button pair as `footer={<>…</>}` — without unwrapping, `split` would put
 * both buttons inside a single flex-1 wrapper (a plain column: stacked,
 * full-width, no gap) instead of stretching each one.
 */
function flattenFragments(children: ReactNode): ReactNode[] {
  return Children.toArray(children).flatMap((child) =>
    isValidElement(child) && child.type === Fragment
      ? Children.toArray((child.props as { children?: ReactNode }).children)
      : [child],
  );
}

export function DialogFooter({
  children,
  layout,
  className,
  testID,
}: {
  children?: ReactNode;
  layout?: NonNullable<DialogFooterVariantProps['layout']>;
  className?: string;
  /** Defaults to the dialog's `<testID>-footer`. */
  testID?: string;
}) {
  const { align, testID: rootTestID } = useDialogContext();
  const resolved = layout ?? (align === 'center' ? 'split' : 'end');
  return (
    <View
      testID={testID ?? subTestID(rootTestID, 'footer')}
      className={cn(dialogFooterVariants({ layout: resolved }), className)}>
      {resolved === 'split'
        ? flattenFragments(children).map((child, index) => (
            <View key={index} className="min-w-0 flex-1">
              {child}
            </View>
          ))
        : children}
    </View>
  );
}

/**
 * Full-width media block (Figma Header slot=Image / Image +Text body):
 * 24px radius, secondary-200 border, clipped. Size the content yourself
 * (e.g. `className="h-60 w-full object-cover"` on an img).
 */
export function DialogMedia({
  children,
  className,
}: {
  children?: ReactNode;
  className?: string;
}) {
  return <View className={cn(DIALOG_HEADER_MEDIA_CLASS, className)}>{children}</View>;
}

/** 44px icon chip (Figma Header slot=Icon): secondary-200 fill, 16px radius. */
export function DialogHeaderIcon({
  icon: Icon,
  className,
}: {
  icon: IconComponent;
  className?: string;
}) {
  const { isDark } = useTheme();
  return (
    <View className={cn(DIALOG_HEADER_ICON_CHIP_CLASS, className)}>
      {/* Web: currentColor via the auto-inverting token class (follows any
          `.dark` scope, incl. Storybook split panes). Native: explicit hex —
          lucide-react-native ignores className/currentColor. Matches title. */}
      {Platform.OS === 'web' ? (
        <Icon size={DIALOG_HEADER_ICON_SIZE} className="text-secondary-950" />
      ) : (
        <Icon size={DIALOG_HEADER_ICON_SIZE} color={isDark ? '#fafafa' : '#0a0a0a'} />
      )}
    </View>
  );
}

/**
 * The 36px ghost X button (the DS Icon Button). Presses the Dialog's
 * `onClose` from context unless an explicit `onPress` is given. In composed
 * children, wrap it in `DialogTopRight` (or your own header row) — a Dialog's
 * close button always lives in the top-right corner.
 */
export function DialogClose({
  onPress,
  accessibilityLabel = 'Close',
  variant = 'ghost',
  testID,
}: {
  onPress?: () => void;
  accessibilityLabel?: string;
  /** `secondary`/`tertiary` give the X a filled chip, used over media headers. */
  variant?: 'ghost' | 'secondary' | 'tertiary';
  /** Defaults to the dialog's `<testID>-close`. */
  testID?: string;
}) {
  const { onClose, testID: rootTestID } = useDialogContext();
  return (
    <Button
      testID={testID ?? subTestID(rootTestID, 'close')}
      variant={variant}
      size="s"
      icon={X}
      accessibilityLabel={accessibilityLabel}
      onPress={onPress ?? onClose}
    />
  );
}

/**
 * The 36px back-arrow button (Figma Header slot=Back button), ghost by
 * default. Wrap it in `DialogTopLeft` to pin it in the panel's corner.
 */
export function DialogBack({
  onPress,
  accessibilityLabel = 'Back',
  variant = 'ghost',
  testID,
}: {
  onPress?: () => void;
  accessibilityLabel?: string;
  /** `secondary`/`tertiary` give the arrow a filled chip, used over media headers. */
  variant?: 'ghost' | 'secondary' | 'tertiary';
  /** Defaults to the dialog's `<testID>-back`. */
  testID?: string;
}) {
  const { testID: rootTestID } = useDialogContext();
  return (
    <Button
      testID={testID ?? subTestID(rootTestID, 'back')}
      variant={variant}
      size="s"
      icon={ArrowLeft}
      accessibilityLabel={accessibilityLabel}
      onPress={onPress}
    />
  );
}

/**
 * Where a corner-pinned control sits vertically.
 *
 * `title` (default) — level with a `DialogTitle` that sits in the flow at the
 * panel's 24px padding. The DS icon button is 36px tall while the H1 title
 * line box is 32px, so a control pinned at the plain 24px inset renders its
 * icon 2px BELOW the title's optical centre. Pinning at 22px puts the 20px
 * glyph's centre on the title line's centre (24 + 16 − 18 = 22). See
 * `DIALOG_TITLE_CONTROLS_CLASS` for the in-flow twin of this rule.
 *
 * `corner` — the plain 24px padding inset, for a control floating over a
 * full-bleed surface where there is no title to line up with.
 *
 * `media` — 8px inside a header media block's corner: at the plain inset the
 * button's hover fill met the media's 24px rounded corner and read as cut off.
 */
export type DialogCornerAlign = 'title' | 'corner' | 'media';

const CORNER_TOP_CLASS: Record<DialogCornerAlign, string> = {
  title: 'top-[22px]',
  corner: 'top-6',
  media: 'top-8',
};
const CORNER_RIGHT_CLASS: Record<DialogCornerAlign, string> = {
  title: 'right-6',
  corner: 'right-6',
  media: 'right-8',
};
const CORNER_LEFT_CLASS: Record<DialogCornerAlign, string> = {
  title: 'left-6',
  corner: 'left-6',
  media: 'left-8',
};

/**
 * Pins its child (typically `DialogClose`) to the panel's top-right corner —
 * the Dialog close button ALWAYS sits top-right regardless of what the header
 * row contains. Defaults to `align="title"`, so a composed dialog that puts a
 * `DialogTitle` in the flow gets the X lined up on it without touching this.
 */
export function DialogTopRight({
  children,
  align = 'title',
}: {
  children?: ReactNode;
  align?: DialogCornerAlign;
}) {
  return (
    <View
      className={cn('absolute z-10', CORNER_RIGHT_CLASS[align], CORNER_TOP_CLASS[align])}>
      {children}
    </View>
  );
}

/**
 * Mirror of `DialogTopRight` for the panel's top-LEFT corner — the Figma
 * navigation row (node 538:14518) puts a `DialogBack` there, level with the
 * close X, while centered media/title flow underneath. Use it when the back
 * arrow can't sit inline in the title row (the prop-driven `onBack` layout).
 */
export function DialogTopLeft({
  children,
  align = 'title',
}: {
  children?: ReactNode;
  align?: DialogCornerAlign;
}) {
  return (
    <View
      className={cn('absolute z-10', CORNER_LEFT_CLASS[align], CORNER_TOP_CLASS[align])}>
      {children}
    </View>
  );
}
